import { JSX, useEffect, useMemo, useRef } from "react";

import { PieArcDatum, arc, pie, scaleOrdinal, select } from "d3";

export type ChartEntry = {
	label: string;
	value: number;
	colour: string;
};

export type PieChartViewProps = {
	data: Array<ChartEntry>;
};

const CHART_AREA = 76;
const CHART_RADIUS = CHART_AREA / 2;

const MARGIN = 2;
const LABEL_INNER_RADIUS = CHART_RADIUS + MARGIN;
const LABEL_OUTER_RADIUS = 50 - MARGIN;

const PIE_FN = pie<ChartEntry>()
	.sort(null)
	.value(d => d.value);

const ARC_FN = arc<PieArcDatum<ChartEntry>>()
	.innerRadius(0)
	.outerRadius(CHART_RADIUS);

const LABEL_FN = arc<PieArcDatum<ChartEntry>>()
	.innerRadius(LABEL_INNER_RADIUS)
	.outerRadius(LABEL_OUTER_RADIUS);

export default function PieChartView(
	props: Readonly<PieChartViewProps>
): JSX.Element {
	const svgRef = useRef<SVGSVGElement>(null);

	const total = useMemo(
		() => props.data.reduce((a, b) => a + b.value, 0),
		[props.data]
	);

	function drawChart(): void {
		const svg = select(svgRef.current)
			.attr("width", "100%")
			.attr("viewBox", "-50 -50 100 100")
			.attr("style", "font: 5px sans-serif;");
		svg.selectAll("*").remove();

		const chart = PIE_FN(props.data);

		const color = scaleOrdinal<string>()
			.domain(props.data.map(d => d.label))
			.range(props.data.map(d => d.colour));

		svg.append("g")
			.attr("stroke", "white")
			.selectAll()
			.data(chart)
			.join("path")
			.attr("fill", d => color(d.data.label))
			.attr("d", ARC_FN);

		svg.append("g")
			.attr("text-anchor", "middle")
			.selectAll()
			.data(chart)
			.join("text")
			.attr("transform", d => `translate(${LABEL_FN.centroid(d)})`)
			.call(text =>
				text
					.filter(d => d.value > 0)
					.append("tspan")
					.attr("x", 0)
					.text(d => `${((d.value * 100) / total).toFixed(0)}%`)
			);
	}

	useEffect(() => {
		if (svgRef.current === null) {
			return;
		}

		drawChart();
	}, [svgRef.current]);

	useEffect(() => {
		drawChart();
	}, [props.data]);

	return <svg ref={svgRef} />;
}
